import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import type { PlaygroundSessionState } from "src/agents/AgentVersionPage/PlaygroundContext";
import { agentExecutionApi } from "src/api/endpoints";
import { useCurrentWorkspace } from "src/api/queries";
import type {
  HumanMessage,
  SessionResponse,
} from "src/clients/agent-management";

const PREFIX_CACHE_KEY = "agent-execution";

const executionKeys = {
  all: (wsId: string) => [PREFIX_CACHE_KEY, wsId] as const,
  session: (wsId: string, sessionId: string) =>
    [...executionKeys.all(wsId), "sessions", sessionId] as const,
};

export const useAgentExecutionClient = () => {
  const { data: workspace } = useCurrentWorkspace();

  const client = useMemo(
    () =>
      workspace?.base_url ? agentExecutionApi(workspace.base_url) : undefined,
    [workspace?.base_url],
  );

  return { client, workspace };
};

type UseAgentSessionOptions = {
  /** Whether to poll for updates. Defaults to true. Set to false to disable polling. */
  polling: boolean;
};

export const useAgentSession = (
  sessionId: string | null,
  options: UseAgentSessionOptions,
) => {
  const { polling } = options;
  const { client, workspace } = useAgentExecutionClient();
  const wsId = workspace?.id ?? "";

  return useQuery({
    queryKey: executionKeys.session(wsId, sessionId ?? ""),
    queryFn: async (): Promise<SessionResponse> => {
      if (!client) {
        throw new Error("Agent execution client not available");
      }
      if (!sessionId) {
        throw new Error("Session ID is required");
      }
      const response =
        await client.getSessionAgentsManagementApiV0SessionsSessionIdGet({
          sessionId,
          includeMessages: true,
        });
      return response.data;
    },
    enabled: !!client && !!sessionId,
    refetchInterval: (query) => {
      if (!polling) {
        return false;
      }
      // Stop polling on error
      if (query.state.status === "error") {
        return false;
      }
      // Stop polling when session is complete
      const data = query.state.data;
      if (
        data?.status === "success" ||
        data?.status === "failed" ||
        data?.status === "cancelled"
      ) {
        return false;
      }
      // Poll every 1 second while running
      return 1000;
    },
    throwOnError: false,
  });
};

export const useSessionToPlaygroundState = (
  session: SessionResponse | undefined,
  sessionId: string | null,
): PlaygroundSessionState | null =>
  useMemo((): PlaygroundSessionState | null => {
    if (!session || !sessionId) return null;

    const {
      status,
      termination_reason,
      termination_message,
      messages,
      result,
    } = session;
    const sessionMessages = messages ?? [];

    const inputSchema =
      (session.agent_version_config?.input_schema as
        | Record<string, unknown>
        | undefined) ?? null;

    const firstHumanMessage = sessionMessages.find((m) => m.type === "human") as
      | HumanMessage
      | undefined;

    const structuredInput = firstHumanMessage?.structured_input ?? {};

    const baseState = {
      sessionId,
      sessionMessages,
      inputSchema,
      submittedInput: {
        structuredInput,
        instructions: firstHumanMessage?.invocation_message ?? "",
      },
    };

    if (status === "success" && result) {
      return { ...baseState, status: "stopped", substatus: "success", result };
    }
    if (status === "failed") {
      return {
        ...baseState,
        status: "stopped",
        substatus: "failed",
        terminationReason: termination_reason ?? "error",
        terminationMessage: termination_message ?? null,
      };
    }
    if (status === "cancelled") {
      return { ...baseState, status: "stopped", substatus: "cancelled" };
    }
    if (status === "paused") {
      return { ...baseState, status: "active", substatus: "paused" };
    }
    // pending or running
    return { ...baseState, status: "active", substatus: "running" };
  }, [session, sessionId]);
